/**
 * listening-exam — 英語聽力卷線上互動卷 + 純計分函式。
 *
 * 單一真相:docs/spec/LISTENING_EXAM_SPEC.md §4。四題型互動作答 → 交卷即時計分 +
 * 逐題對錯 + 正解。純計分函式 `scoreListeningExam` 抽出可單測。
 *
 * 視覺 = 學術風格設計系統(src/learn/ui):紙感頁殼 `pageShell` + 學術按鈕
 * `academicButton`。專業介面鐵則:無音效(UI 提示音)、無 mascot、無彩光、無戲謔
 * 文案;朗讀本身是功能。
 *
 * 音檔:靜態優先(public/audio/listening/<examId>/<id>.mp3),缺檔 fallback
 * `speak(spoken, { voice:'cloud' })`(同 oral-exam 的 playDemo 慣例)。
 *
 * 學習/遊戲分離:純學習側,只動學習數據,**絕不**碰 commanderXp / 戰鬥數值。
 */

import type {
  ListeningExam,
  ListeningPart,
} from './listening-exam-types';
import { speak, stopSpeaking } from '../../shared/tts-client';
import { pageShell, academicButton } from '../ui/components';

/** 念法1(雲端 Aura-2)隨機嗓池(同 dictation:排除被嫌棄的 luna)。 */
const CLOUD_SPEAKERS = ['asteria', 'thalia', 'andromeda', 'athena', 'orion'] as const;

function pickCloudSpeaker(): string {
  return CLOUD_SPEAKERS[Math.floor(Math.random() * CLOUD_SPEAKERS.length)];
}

/** 一題對應的離線預產音檔路徑(public/ 之下,Vite 以根目錄提供)。 */
export function listeningAudioUrl(examId: string, audioId: string): string {
  return `/audio/listening/${examId}/${audioId}.mp3`;
}

// ───────────────────────────── 純計分 ─────────────────────────────

/** 學生對單一 part 的作答:`itemId → 選擇`(index 或 'O'/'X')。 */
export type PartAnswers = Record<string, number | 'O' | 'X' | undefined>;

/** 整卷作答:`partIndex → PartAnswers`。 */
export type ExamAnswers = Record<number, PartAnswers>;

/** 單題判定結果。 */
export interface ItemResult {
  itemId: string;
  correct: boolean;
  /** 正解的人類可讀字串(顯示用)。 */
  correctLabel: string;
  /** 學生選的(未作答 = undefined)。 */
  chosenLabel: string | undefined;
  /** 該題配分。 */
  points: number;
  /** 答對得到的分(對 = points,錯 / 未答 = 0)。 */
  earned: number;
}

/** 整卷計分結果。 */
export interface ExamScore {
  /** 得分。 */
  score: number;
  /** 滿分(Σ part.pointsEach × 題數)。 */
  total: number;
  /** 答對題數。 */
  correctCount: number;
  /** 總題數。 */
  itemCount: number;
  /** 逐題結果(依 part / item 順序)。 */
  results: ItemResult[];
}

/** 一題的正解 label(顯示用)。 */
function correctLabelOf(part: ListeningPart, itemIdx: number): string {
  switch (part.kind) {
    case 'listen-check':
      return part.items[itemIdx].options[part.items[itemIdx].answerIndex];
    case 'listen-choose':
      return part.items[itemIdx].choices[part.items[itemIdx].answerIndex];
    case 'listen-mark':
      return part.items[itemIdx].answer === 'O' ? '○' : '×';
    case 'listen-circle':
      return part.items[itemIdx].options[part.items[itemIdx].answerIndex];
  }
}

/** 學生選擇 → 顯示 label(未作答 = undefined)。 */
function chosenLabelOf(
  part: ListeningPart,
  itemIdx: number,
  chosen: number | 'O' | 'X' | undefined,
): string | undefined {
  if (chosen === undefined) return undefined;
  switch (part.kind) {
    case 'listen-check':
      return typeof chosen === 'number' ? part.items[itemIdx].options[chosen] : undefined;
    case 'listen-choose':
      return typeof chosen === 'number' ? part.items[itemIdx].choices[chosen] : undefined;
    case 'listen-mark':
      return chosen === 'O' ? '○' : chosen === 'X' ? '×' : undefined;
    case 'listen-circle':
      return typeof chosen === 'number' ? part.items[itemIdx].options[chosen] : undefined;
  }
}

/** 一題是否答對。 */
function isItemCorrect(
  part: ListeningPart,
  itemIdx: number,
  chosen: number | 'O' | 'X' | undefined,
): boolean {
  if (chosen === undefined) return false;
  switch (part.kind) {
    case 'listen-check':
      return chosen === part.items[itemIdx].answerIndex;
    case 'listen-choose':
      return chosen === part.items[itemIdx].answerIndex;
    case 'listen-mark':
      return chosen === part.items[itemIdx].answer;
    case 'listen-circle':
      return chosen === part.items[itemIdx].answerIndex;
  }
}

/**
 * 純計分:資料驅動 `Σ part.pointsEach × 答對數`。可單測(無 DOM 依賴)。
 *
 * @param exam    一份聽力卷。
 * @param answers 整卷作答(`partIndex → { itemId → 選擇 }`)。
 * @returns 得分 / 滿分 / 逐題結果。
 */
export function scoreListeningExam(exam: ListeningExam, answers: ExamAnswers): ExamScore {
  const results: ItemResult[] = [];
  let score = 0;
  let total = 0;
  let correctCount = 0;
  let itemCount = 0;

  exam.parts.forEach((part, pIdx) => {
    const partAns = answers[pIdx] ?? {};
    part.items.forEach((item, iIdx) => {
      itemCount += 1;
      total += part.pointsEach;
      const chosen = partAns[item.id];
      const correct = isItemCorrect(part, iIdx, chosen);
      const earned = correct ? part.pointsEach : 0;
      if (correct) {
        correctCount += 1;
        score += part.pointsEach;
      }
      results.push({
        itemId: item.id,
        correct,
        correctLabel: correctLabelOf(part, iIdx),
        chosenLabel: chosenLabelOf(part, iIdx, chosen),
        points: part.pointsEach,
        earned,
      });
    });
  });

  return { score, total, correctCount, itemCount, results };
}

// ───────────────────────────── 互動卷 UI ─────────────────────────────

/**
 * Render 線上聽力卷 into `container`。四題型互動作答 → 「交卷」即時計分 + 逐題對錯。
 *
 * @param exam   一份(定稿或產生)聽力卷。
 * @param onDone 離開 / 完成回呼。
 */
export function runListeningExam(
  container: HTMLElement,
  exam: ListeningExam,
  onDone: () => void,
): void {
  container.innerHTML = '';

  const shell = pageShell(container, {
    title: exam.title,
    subtitle: '每題按 🔊 聽,作答後按「交卷」看分數與正解。可重播 / 慢速 / 換念法。',
    // 答題畫面:統一 ‹ 返回 / 左緣滑動先確認;返回時停音檔/TTS。confirm 通過才走 onBack。
    onBack: () => { cleanup(); onDone(); },
    confirmOnBack: '離開後這張卷的作答不會保留,確定?',
  });

  const root = document.createElement('div');
  root.className = 'listening-exam';
  root.style.cssText = 'display:flex;flex-direction:column;gap:22px;';
  shell.body.appendChild(root);

  // 學生作答狀態:partIndex → { itemId → 選擇 }。
  const answers: ExamAnswers = {};

  let submitted = false;
  let currentAudio: HTMLAudioElement | null = null;

  const cleanup = (): void => {
    stopSpeaking();
    if (currentAudio) {
      try { currentAudio.pause(); } catch { /* noop */ }
      currentAudio = null;
    }
    shell.unmount();
  };

  /**
   * 播一題:靜態 mp3 優先,抓不到(404 / 不能播)fallback 即時 cloud TTS。
   * `rate` < 1 = 慢速;`voice='device'` 換念法2(清晰)。
   */
  const playAudio = (
    audioId: string,
    spoken: string,
    rate = 1,
    voice: 'cloud' | 'device' = 'cloud',
  ): void => {
    stopSpeaking();
    if (currentAudio) { try { currentAudio.pause(); } catch { /* noop */ } currentAudio = null; }

    // 念法2(device)或慢速:直接走 speak(裝置端 rate 較可靠),不讀靜態檔。
    if (voice === 'device' || rate !== 1) {
      void speak(spoken, { lang: 'en', rate, voice });
      return;
    }

    let fellBack = false;
    const fallback = (): void => {
      if (fellBack) return;
      fellBack = true;
      void speak(spoken, { lang: 'en', voice: 'cloud', speaker: pickCloudSpeaker() });
    };
    let audio: HTMLAudioElement;
    try {
      audio = new Audio(listeningAudioUrl(exam.id, audioId));
    } catch { fallback(); return; }
    currentAudio = audio;
    audio.addEventListener('error', fallback, { once: true });
    const p = audio.play() as unknown as Promise<void> | undefined;
    if (p && typeof p.then === 'function') p.catch(fallback);
  };

  // ── 各題型 render ──
  exam.parts.forEach((part, pIdx) => {
    answers[pIdx] = {};
    const section = document.createElement('section');
    section.className = `lx-part lx-part--${part.kind}`;
    section.style.cssText = 'display:flex;flex-direction:column;gap:14px;';

    const head = document.createElement('div');
    head.className = 'lx-part-head';
    head.style.cssText =
      'font-family:var(--ef-serif);font-weight:700;font-size:18px;color:var(--ef-ink);' +
      'border-left:4px solid var(--ef-accent,#2f6b4f);padding-left:10px;';
    head.textContent = `${part.label} · ${part.title}`;
    section.appendChild(head);

    const instr = document.createElement('div');
    instr.className = 'lx-part-instr';
    instr.style.cssText = 'font-size:14px;color:var(--ef-sub);line-height:1.6;';
    instr.textContent = part.instructions;
    section.appendChild(instr);

    part.items.forEach((item, iIdx) => {
      const row = renderItem(part, pIdx, iIdx, item, answers, playAudio);
      section.appendChild(row);
    });

    root.appendChild(section);
  });

  // ── 交卷 + 結果區 ──
  const actions = document.createElement('div');
  actions.className = 'lx-actions';
  actions.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;margin-top:8px;';
  root.appendChild(actions);

  const resultEl = document.createElement('div');
  resultEl.className = 'lx-result';
  resultEl.style.cssText = 'display:none;flex-direction:column;gap:10px;';
  root.appendChild(resultEl);

  const submitBtn = academicButton({
    label: '交卷',
    variant: 'primary',
    onClick: () => {
      if (submitted) return;
      submitted = true;
      const result = scoreListeningExam(exam, answers);
      renderResult(resultEl, result);
      submitBtn.disabled = true;
      submitBtn.classList.add('lx-submit--done');
      resultEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  });
  submitBtn.classList.add('lx-submit');
  actions.appendChild(submitBtn);

  const doneBtn = academicButton({
    label: '完成',
    variant: 'secondary',
    onClick: () => {
      cleanup();
      onDone();
    },
  });
  doneBtn.classList.add('lx-done');
  actions.appendChild(doneBtn);
}

/** 一題的容器(題幹 + 🔊 播放控制 + 作答 UI)。 */
function renderItem(
  part: ListeningPart,
  pIdx: number,
  iIdx: number,
  item: ListeningPart['items'][number],
  answers: ExamAnswers,
  playAudio: (audioId: string, spoken: string, rate?: number, voice?: 'cloud' | 'device') => void,
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'lx-item';
  row.dataset.itemId = item.id;
  row.style.cssText =
    'display:flex;flex-direction:column;gap:10px;padding:14px 16px;' +
    'background:var(--ef-paper-bright,#fff);border:1px solid var(--ef-line,#d8d2c4);' +
    'border-radius:var(--ef-radius,8px);box-shadow:var(--ef-shadow-card,none);';

  // 題號 + 播放控制列。
  const top = document.createElement('div');
  top.className = 'lx-item-top';
  top.style.cssText = 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;';

  const num = document.createElement('span');
  num.className = 'lx-item-num';
  num.style.cssText = 'font-weight:700;min-width:1.6em;color:var(--ef-ink);';
  num.textContent = `${iIdx + 1}.`;
  top.appendChild(num);

  // audio 取得(各題型都有 .audio)。
  const audio = item.audio;
  const playBtn = makeSmallBtn('🔊 播放', `lx-play lx-play--${item.id}`);
  playBtn.addEventListener('click', () => playAudio(audio.id, audio.spoken, 1, 'cloud'));
  const replayBtn = makeSmallBtn('🐢 慢速', `lx-slow lx-slow--${item.id}`);
  replayBtn.addEventListener('click', () => playAudio(audio.id, audio.spoken, 0.6, 'device'));
  const voice2Btn = makeSmallBtn('🔁 換念法', `lx-voice2 lx-voice2--${item.id}`);
  voice2Btn.addEventListener('click', () => playAudio(audio.id, audio.spoken, 1, 'device'));
  top.append(playBtn, replayBtn, voice2Btn);
  row.appendChild(top);

  // 作答 UI(依題型)。
  const body = document.createElement('div');
  body.className = 'lx-item-body';
  body.style.cssText = 'display:flex;flex-wrap:wrap;gap:10px;align-items:center;';

  if (part.kind === 'listen-check') {
    const it = part.items[iIdx];
    it.options.forEach((opt, oi) => {
      const btn = makeChoiceBtn(opt, `lx-opt lx-opt--${item.id}-${oi}`);
      btn.addEventListener('click', () => {
        answers[pIdx][item.id] = oi;
        markSelected(body, btn);
      });
      body.appendChild(btn);
    });
  } else if (part.kind === 'listen-choose') {
    const it = part.items[iIdx];
    it.choices.forEach((choice, oi) => {
      const btn = makeChoiceBtn(`${oi + 1}. ${choice}`, `lx-opt lx-opt--${item.id}-${oi}`);
      btn.addEventListener('click', () => {
        answers[pIdx][item.id] = oi;
        markSelected(body, btn);
      });
      body.appendChild(btn);
    });
  } else if (part.kind === 'listen-mark') {
    const it = part.items[iIdx];
    const printed = document.createElement('span');
    printed.className = 'lx-printed';
    printed.style.cssText = 'font-size:15px;color:var(--ef-ink);margin-right:8px;';
    printed.textContent = it.printed;
    body.appendChild(printed);
    (['O', 'X'] as const).forEach((mark) => {
      const btn = makeChoiceBtn(mark === 'O' ? '○ 相同' : '× 不同', `lx-opt lx-opt--${item.id}-${mark}`);
      btn.addEventListener('click', () => {
        answers[pIdx][item.id] = mark;
        markSelected(body, btn);
      });
      body.appendChild(btn);
    });
  } else {
    // listen-circle
    const it = part.items[iIdx];
    if (it.before) {
      const bf = document.createElement('span');
      bf.className = 'lx-circle-before';
      bf.style.cssText = 'font-size:15px;color:var(--ef-ink);';
      bf.textContent = it.before;
      body.appendChild(bf);
    }
    it.options.forEach((opt, oi) => {
      const btn = makeChoiceBtn(opt, `lx-opt lx-opt--${item.id}-${oi}`);
      btn.addEventListener('click', () => {
        answers[pIdx][item.id] = oi;
        markSelected(body, btn);
      });
      body.appendChild(btn);
    });
    if (it.after) {
      const af = document.createElement('span');
      af.className = 'lx-circle-after';
      af.style.cssText = 'font-size:15px;color:var(--ef-ink);';
      af.textContent = it.after;
      body.appendChild(af);
    }
  }

  row.appendChild(body);
  return row;
}

/** 選項二態:把同組其他鈕的 selected 拿掉,標記這顆。 */
function markSelected(group: HTMLElement, btn: HTMLButtonElement): void {
  group.querySelectorAll('.lx-opt').forEach((b) => b.classList.remove('lx-opt--selected'));
  btn.classList.add('lx-opt--selected');
}

/** 結果區:總分 + 逐題對錯 + 正解。 */
function renderResult(el: HTMLElement, result: ExamScore): void {
  el.innerHTML = '';
  el.style.display = 'flex';

  const headline = document.createElement('div');
  headline.className = 'lx-score';
  headline.style.cssText =
    'font-family:var(--ef-serif);font-weight:700;font-size:24px;color:var(--ef-ink);' +
    'border-top:2px solid var(--ef-line,#d8d2c4);padding-top:14px;';
  headline.textContent = `分數:${result.score} / ${result.total}(答對 ${result.correctCount} / ${result.itemCount} 題)`;
  el.appendChild(headline);

  const list = document.createElement('div');
  list.className = 'lx-result-list';
  list.style.cssText = 'display:flex;flex-direction:column;gap:6px;font-size:14px;';

  let runningIdx = 0;
  result.results.forEach((r) => {
    runningIdx += 1;
    const line = document.createElement('div');
    line.className = `lx-result-line ${r.correct ? 'lx-correct' : 'lx-wrong'}`;
    line.style.cssText = 'display:flex;gap:8px;align-items:baseline;';
    const mark = r.correct ? '✓' : '✗';
    const chosen = r.chosenLabel ?? '(未作答)';
    const detail = r.correct
      ? `${chosen}`
      : `你選「${chosen}」→ 正解「${r.correctLabel}」`;
    line.textContent = `${mark} 第 ${runningIdx} 題:${detail}`;
    line.style.color = r.correct ? 'var(--ef-ink)' : '#b03030';
    list.appendChild(line);
  });
  el.appendChild(list);
}

/** 小播放控制鈕(secondary 學術按鈕 + 行為 class)。 */
function makeSmallBtn(label: string, className: string): HTMLButtonElement {
  const btn = academicButton({ label, variant: 'secondary', onClick: () => {} });
  for (const c of className.split(' ')) btn.classList.add(c);
  return btn;
}

/** 選項鈕(secondary 學術按鈕 + 行為 class)。 */
function makeChoiceBtn(label: string, className: string): HTMLButtonElement {
  const btn = academicButton({ label, variant: 'secondary', onClick: () => {} });
  for (const c of className.split(' ')) btn.classList.add(c);
  return btn;
}
